/**
 * AI Integration Type Definitions
 * Enterprise-grade types for Spring AI backend integration
 * 
 * @version 1.0.0
 * @author JGS Tech Team
 */

import { PostType } from './postTypes';
import { CategoryPayload } from './articleTypes';

// ================== CORE AI TYPES ==================

export enum AIServiceType {
  CHATBOT = 'CHATBOT',
  CONTENT_GENERATION = 'CONTENT_GENERATION',
  CONTENT_ANALYSIS = 'CONTENT_ANALYSIS',
  STRUCTURED_CONTENT = 'STRUCTURED_CONTENT'
}

export enum AIModelType {
  GPT_4 = 'GPT_4',
  CLAUDE = 'CLAUDE',
  GEMINI = 'GEMINI',
  CUSTOM = 'CUSTOM'
}

export enum ConversationStatus {
  ACTIVE = 'ACTIVE',
  PAUSED = 'PAUSED',
  COMPLETED = 'COMPLETED',
  ERROR = 'ERROR'
}

export enum MessageType {
  USER = 'USER',
  AI = 'AI',
  SYSTEM = 'SYSTEM',
  ERROR = 'ERROR'
}

export enum MessageStatus {
  SENDING = 'SENDING',
  SENT = 'SENT',
  DELIVERED = 'DELIVERED',
  ERROR = 'ERROR',
  RETRYING = 'RETRYING'
}

// ================== CONVERSATION MANAGEMENT ==================

export interface AIMessage {
  id: string;
  conversationId: string;
  type: MessageType;
  content: string;
  timestamp: Date;
  status: MessageStatus;
  metadata?: {
    model?: AIModelType;
    tokens?: number;
    processingTime?: number;
    confidence?: number;
    sources?: string[];
    language?: string;
    sentiment?: 'positive' | 'negative' | 'neutral';
  };
  error?: {
    code: string;
    message: string;
    retryable: boolean;
  };
}

export interface AIConversation {
  id: string;
  title?: string;
  topic?: string;
  language: string;
  status: ConversationStatus;
  messages: AIMessage[];
  createdAt: Date;
  lastActivity: Date;
  metadata?: {
    model: AIModelType;
    totalMessages: number;
    totalTokens: number;
    averageResponseTime: number;
    userSatisfaction?: number;
    tags?: string[];
  };
  context?: {
    userId?: string;
    sessionId?: string;
    contentType?: PostType;
    category?: string;
    region?: string;
  };
}

// ================== STRUCTURED CONTENT GENERATION ==================

export interface StructuredContentRequest {
  inputContent: string;
  language: string;
  contentType?: string;
  audience?: string;
  options?: {
    tone?: 'formal' | 'casual' | 'professional' | 'friendly';
    length?: 'short' | 'medium' | 'long';
    includeKeywords?: string[];
    excludeContent?: string[];
    format?: 'news' | 'blog' | 'social' | 'technical';
  };
}

export interface StructuredContentResponse {
  id: string;
  title: string;
  description: string;
  body: string;
  language: string;
  category: string;
  tags: string[];
  summary: string;
  metadata: {
    wordCount: number;
    readingTime: number;
    seoScore?: number;
    sentiment: string;
    topics: string[];
    generatedAt: Date;
    model: AIModelType;
    processingTime: number;
  };
  suggestions?: {
    alternativeTitles?: string[];
    relatedTopics?: string[];
    improvementTips?: string[];
  };
}

// ================== CONTENT ANALYSIS ==================

export interface ContentAnalysisRequest {
  content: string;
  analysisType: 'summarize' | 'fact-check' | 'headlines' | 'sentiment' | 'keywords';
  language?: string;
  options?: {
    maxLength?: number;
    includeStatistics?: boolean;
    includeSources?: boolean;
    confidenceThreshold?: number;
  };
}

export interface ContentAnalysisResponse {
  id: string;
  analysisType: string;
  result: any; // Varies by analysis type
  confidence: number;
  metadata: {
    processingTime: number;
    model: AIModelType;
    language: string;
    analysedAt: Date;
  };
  warnings?: string[];
  limitations?: string[];
}

// ================== FORM INTEGRATION ==================

export interface AIEnhancedArticleForm {
  // Standard article fields
  title: string;
  description: string;
  body: string;
  tags: string[];
  postType?: PostType;
  categories: CategoryPayload[];
  
  // AI enhancement fields
  aiGenerated: boolean;
  aiGenerationId?: string;
  aiSuggestions?: {
    alternativeTitles: string[];
    additionalTags: string[];
    relatedCategories: string[];
    contentImprovements: string[];
  };
  aiMetadata?: {
    originalContent?: string;
    generationPrompt?: string;
    model: AIModelType;
    confidence: number;
    language: string;
  };
}

// ================== API INTERFACES ==================

export interface AIApiConfig {
  baseURL: string;
  timeout: number;
  retryAttempts: number;
  apiKey?: string;
  endpoints: {
    chatContextual: string;
    chatSimple: string;
    clearChat: string;
    activeConversations: string;
    structuredContent: string;
    quickStructuredContent: string;
    summarize: string;
    factCheck: string;
    generateHeadlines: string;
    generateArticle: string;
    health: string;
    // TTS endpoints
    ttsGenerate: string;
    ttsGenerateNews: string;
    ttsGenerateArticle: string;
    ttsVoices: string;
    ttsLanguages: string;
    ttsVoiceRecommend: string;
    ttsHealth: string;
  };
}

export interface AIApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
    details?: any;
  };
  metadata?: {
    timestamp: Date;
    requestId: string;
    processingTime: number;
    rateLimit?: {
      remaining: number;
      reset: Date;
    };
  };
}

// ================== ERROR HANDLING ==================

export enum AIErrorCode {
  NETWORK_ERROR = 'NETWORK_ERROR',
  TIMEOUT_ERROR = 'TIMEOUT_ERROR',
  RATE_LIMIT_EXCEEDED = 'RATE_LIMIT_EXCEEDED',
  INVALID_INPUT = 'INVALID_INPUT',
  MODEL_UNAVAILABLE = 'MODEL_UNAVAILABLE',
  AUTHENTICATION_FAILED = 'AUTHENTICATION_FAILED',
  QUOTA_EXCEEDED = 'QUOTA_EXCEEDED',
  CONTENT_POLICY_VIOLATION = 'CONTENT_POLICY_VIOLATION',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR'
}

export class AIError extends Error {
  constructor(
    message: string,
    public code: AIErrorCode,
    public retryable: boolean = false,
    public context?: any
  ) {
    super(message);
    this.name = 'AIError';
  }
}

// ================== HOOKS INTERFACES ==================

export interface UseAIState {
  isLoading: boolean;
  error: AIError | null;
  requestId: string | null;
  lastRequest?: {
    timestamp: Date;
    endpoint: string;
    payload: any;
  };
}

export interface UseChatbotState {
  conversations: Record<string, AIConversation>;
  currentConversationId: string | null;
  isLoading: boolean;
  error: AIError | null;
  connectionStatus: 'connected' | 'disconnected' | 'reconnecting';
}

export interface UseStructuredContentState {
  structuredContent: StructuredContentResponse | null;
  generationHistory: StructuredContentResponse[];
  isGenerating: boolean;
  error: AIError | null;
  quota?: {
    used: number;
    limit: number;
    resetDate: Date;
  };
}

// ================== COMPONENT PROPS ==================

export interface ChatbotProps {
  theme?: 'light' | 'dark';
  languages?: string[];
  defaultLanguage?: string;
  showHistory?: boolean;
  maxConversations?: number;
  autoSave?: boolean;
  placeholder?: string;
  className?: string;
  onConversationStart?: (conversation: AIConversation) => void;
  onConversationEnd?: (conversation: AIConversation) => void;
  onError?: (error: AIError) => void;
}

export interface StructuredContentGeneratorProps {
  onContentGenerated?: (content: StructuredContentResponse) => void;
  onError?: (error: AIError) => void;
  defaultLanguage?: string;
  maxInputLength?: number;
  showAdvancedOptions?: boolean;
  allowHistoryAccess?: boolean;
  className?: string;
  theme?: 'light' | 'dark';
}

export interface AIEnhancedFormProps {
  postType?: PostType;
  existingContent?: Partial<AIEnhancedArticleForm>;
  onContentUpdate?: (content: AIEnhancedArticleForm) => void;
  enableRealTimeAnalysis?: boolean;
  showAISuggestions?: boolean;
  className?: string;
}

// ================== UTILITY TYPES ==================

export type SupportedLanguage = 
  | 'English'
  | 'Hindi'
  | 'Bengali'
  | 'Telugu'
  | 'Marathi'
  | 'Tamil'
  | 'Gujarati'
  | 'Kannada'
  | 'Odia'
  | 'Punjabi'
  | 'Malayalam'
  | 'Assamese'
  | 'Urdu'
  | 'Spanish'
  | 'French'
  | 'German'
  | 'Chinese'
  | 'Arabic';

export type ContentType = 
  | 'news article'
  | 'blog post'
  | 'social media post'
  | 'press release'
  | 'informative article'
  | 'opinion piece'
  | 'feature story'
  | 'technical documentation'
  | 'marketing copy';

export type AudienceType = 
  | 'general readers'
  | 'professionals'
  | 'students'
  | 'experts'
  | 'children'
  | 'seniors'
  | 'technical audience'
  | 'business audience';

// ================== TEXT-TO-SPEECH (TTS) TYPES ==================

/**
 * Available TTS voices from OpenAI.
 * Each voice has unique characteristics suitable for different content types.
 */
export type TTSVoice = 
  | 'alloy'     // Balanced, neutral voice
  | 'ash'       // Clear, expressive voice
  | 'ballad'    // Warm, melodic voice
  | 'coral'     // Professional, engaging voice
  | 'echo'      // Deep, resonant voice
  | 'fable'     // Storytelling voice
  | 'onyx'      // Deep, authoritative voice - great for serious news
  | 'nova'      // Professional, clear voice - recommended for news narration
  | 'sage'      // Calm, thoughtful voice
  | 'shimmer'   // Light, friendly voice
  | 'verse';    // Versatile, adaptive voice

/**
 * TTS model options.
 */
export type TTSModel = 'tts-1' | 'tts-1-hd' | 'gpt-4o-mini-tts';

/**
 * Audio output formats supported by TTS.
 * Basic formats plus Eleven Labs extended formats with bitrate specifications.
 */
export type TTSAudioFormat = 
  // Basic formats
  | 'mp3' 
  | 'opus' 
  | 'aac' 
  | 'flac' 
  | 'wav' 
  | 'pcm'
  // Eleven Labs extended MP3 formats (sample_rate_bitrate)
  | 'mp3_22050_32'
  | 'mp3_44100_32'
  | 'mp3_44100_64'
  | 'mp3_44100_96'
  | 'mp3_44100_128'
  | 'mp3_44100_192'
  // Eleven Labs PCM formats
  | 'pcm_16000'
  | 'pcm_22050'
  | 'pcm_24000'
  | 'pcm_44100'
  // Eleven Labs ulaw format
  | 'ulaw_8000';

/**
 * Request payload for TTS audio generation.
 */
export interface TTSRequest {
  /** The text content to convert to speech (max 4096 characters) */
  text: string;
  /** Target language code (e.g., 'en', 'te' for Telugu) */
  language?: string;
  /** Voice selection for audio generation */
  voice?: TTSVoice;
  /** TTS model selection */
  model?: TTSModel;
  /** Audio output format */
  responseFormat?: TTSAudioFormat;
  /** Speech speed multiplier (0.25 to 4.0, default 1.0) */
  speed?: number;
  /** Article ID for association */
  articleId?: string;
  /** Article title for file naming */
  articleTitle?: string;
}

/**
 * Response from TTS audio generation.
 */
export interface TTSResponse {
  /** Whether audio generation was successful */
  success: boolean;
  /** URL to the generated audio file */
  audioUrl?: string;
  /** Original file name of the generated audio */
  fileName?: string;
  /** File size in bytes */
  fileSizeBytes?: number;
  /** Audio duration in seconds (estimated) */
  durationSeconds?: number;
  /** Audio format */
  format?: TTSAudioFormat;
  /** Voice used for generation */
  voiceUsed?: TTSVoice;
  /** Model used for generation */
  modelUsed?: TTSModel;
  /** TTS provider used */
  provider?: string;
  /** Speech speed/rate multiplier used (0.25 to 4.0) */
  speed?: number;
  /** Language of the generated audio */
  language?: string;
  /** Character count of the input text */
  characterCount?: number;
  /** Processing time in milliseconds */
  processingTimeMs?: number;
  /** Timestamp when audio was generated */
  generatedAt?: string;
  /** Associated article ID */
  articleId?: string;
  /** Error message if generation failed */
  errorMessage?: string;
  /** Error code for programmatic handling */
  errorCode?: string;
  /** Additional metadata */
  metadata?: Record<string, unknown>;
}

/**
 * TTS service health status.
 */
export interface TTSHealthStatus {
  service: string;
  status: 'UP' | 'DOWN';
  timestamp: number;
  maxCharacters: number;
  defaultModel: TTSModel;
  defaultVoice: TTSVoice;
}

/**
 * TTS voice information with descriptions.
 */
export interface TTSVoiceInfo {
  voices: TTSVoice[];
  recommendedForNews: TTSVoice;
  voiceDescriptions: Record<TTSVoice, string>;
}

/**
 * TTS language support information.
 */
export interface TTSLanguageInfo {
  languages: string[];
  languageDetails: Record<string, {
    name: string;
    voice: TTSVoice;
    supported: boolean;
  }>;
}

/**
 * TTS configuration state for the UI.
 */
export interface TTSConfig {
  enabled: boolean;
  autoGenerate: boolean;
  defaultVoice: TTSVoice;
  defaultModel: TTSModel;
  defaultFormat: TTSAudioFormat;
  defaultSpeed: number;
  maxCharacters: number;
}

/**
 * TTS generation state for components.
 */
export interface TTSGenerationState {
  isGenerating: boolean;
  progress: number;
  audioUrl: string | null;
  error: string | null;
  lastGeneratedAt: Date | null;
}
// ================== VOICE CONFIGURATION TYPES ==================

/**
 * TTS Provider identifier.
 */
export type TTSProvider = 'openai' | 'elevenlabs' | 'azure' | 'google';

/**
 * Voice configuration entity for dynamic voice selection.
 */
export interface VoiceConfiguration {
  /** Unique voice configuration ID */
  voiceId: number;
  /** Display name for the voice */
  voiceName: string;
  /** TTS provider (openai, elevenlabs, etc.) */
  provider: TTSProvider;
  /** Provider-specific voice ID */
  providerVoiceId: string;
  /** Language code (e.g., 'en', 'te') */
  languageCode: string;
  /** Language display name */
  languageName?: string;
  /** Voice stability setting (0.0 to 1.0) */
  stability?: number;
  /** Similarity boost setting (0.0 to 1.0) */
  similarityBoost?: number;
  /** Style exaggeration setting (0.0 to 1.0) */
  style?: number;
  /** Speech speed/rate multiplier (0.25 to 4.0, default 1.0) */
  speed?: number;
  /** Speaker boost enabled */
  speakerBoost?: boolean;
  /** TTS model ID */
  modelId?: string;
  /** Output audio format */
  outputFormat?: TTSAudioFormat;
  /** Whether this is the default voice for the language */
  isDefault: boolean;
  /** Whether this voice is active */
  isActive: boolean;
  /** Voice category (news, casual, formal, etc.) */
  category?: string;
  /** Voice gender (male, female, neutral) */
  gender?: string;
  /** Sample audio URL for preview */
  sampleAudioUrl?: string;
  /** Display order in UI */
  displayOrder?: number;
  /** Creation timestamp */
  createdAt?: string;
  /** Last update timestamp */
  updatedAt?: string;
}

/**
 * Request payload for creating/updating voice configuration.
 */
export interface VoiceConfigurationPayload {
  voiceName: string;
  provider: TTSProvider;
  providerVoiceId: string;
  languageCode: string;
  languageName?: string;
  stability?: number;
  similarityBoost?: number;
  style?: number;
  speed?: number;
  speakerBoost?: boolean;
  modelId?: string;
  outputFormat?: TTSAudioFormat;
  isDefault?: boolean;
  isActive?: boolean;
  category?: string;
  gender?: string;
  sampleAudioUrl?: string;
  displayOrder?: number;
}

/**
 * Voice configuration search/filter options.
 */
export interface VoiceSearchOptions {
  providers?: TTSProvider[];
  languages?: string[];
  categories?: string[];
  genders?: string[];
  isActive?: boolean;
}

// ================== BACKGROUND MUSIC TYPES ==================

/**
 * Background music category.
 */
export type BGMCategory = 'news' | 'sports' | 'entertainment' | 'business' | 'technology' | 'ambient' | 'upbeat' | 'calm';

/**
 * Background music mood.
 */
export type BGMMood = 'neutral' | 'energetic' | 'calm' | 'dramatic' | 'inspiring' | 'serious' | 'happy';

/**
 * Background music tempo.
 */
export type BGMTempo = 'slow' | 'medium' | 'fast';

/**
 * Background music configuration entity.
 */
export interface BackgroundMusic {
  /** Unique BGM ID */
  bgmId: number;
  /** Display name for the BGM */
  bgmName: string;
  /** BGM category */
  category: BGMCategory;
  /** Description of the BGM */
  description?: string;
  /** File path (server-side) */
  filePath?: string;
  /** File URL for playback */
  fileUrl?: string;
  /** Original filename of uploaded file */
  originalFilename?: string;
  /** Audio format (mp3, wav, etc.) */
  audioFormat?: string;
  /** Duration in seconds */
  durationSeconds?: number;
  /** File size in bytes */
  fileSizeBytes?: number;
  /** Volume level (0.0 to 1.0) */
  volumeLevel: number;
  /** Fade in duration in seconds */
  fadeInDuration?: number;
  /** Fade out duration in seconds */
  fadeOutDuration?: number;
  /** BGM mood */
  mood?: BGMMood;
  /** BGM tempo */
  tempo?: BGMTempo;
  /** Whether the BGM can loop seamlessly */
  isLoopable?: boolean;
  /** Whether this is the default for the category */
  isDefault: boolean;
  /** Whether this BGM is active */
  isActive: boolean;
  /** Tags for search/filtering */
  tags?: string;
  /** Language code for language-specific BGM */
  languageCode?: string;
  /** Display order */
  displayOrder?: number;
  /** Preview URL for short sample */
  previewUrl?: string;
  /** License information */
  licenseInfo?: string;
  /** Creation timestamp */
  createdAt?: string;
  /** Last update timestamp */
  updatedAt?: string;
}

/**
 * Request payload for creating/updating background music.
 */
export interface BackgroundMusicPayload {
  bgmName: string;
  category: BGMCategory;
  description?: string;
  filePath?: string;
  fileUrl?: string;
  originalFilename?: string;
  audioFormat?: string;
  durationSeconds?: number;
  fileSizeBytes?: number;
  volumeLevel?: number;
  fadeInDuration?: number;
  fadeOutDuration?: number;
  mood?: BGMMood;
  tempo?: BGMTempo;
  isLoopable?: boolean;
  isDefault?: boolean;
  isActive?: boolean;
  tags?: string;
  languageCode?: string;
  displayOrder?: number;
  previewUrl?: string;
  licenseInfo?: string;
}

/**
 * BGM search/filter options.
 */
export interface BGMSearchOptions {
  categories?: BGMCategory[];
  moods?: BGMMood[];
  tempos?: BGMTempo[];
  isActive?: boolean;
}

// ==================== JOURNALIST CONTENT GENERATION ====================

export type JournalistLanguage =
  | 'Hindi' | 'Tamil' | 'Telugu' | 'Kannada' | 'Malayalam'
  | 'Marathi' | 'Bengali' | 'Gujarati' | 'Punjabi' | 'Odia'
  | 'Assamese' | 'Urdu' | 'English';

export type JournalistTone =
  | 'Breaking News' | 'Investigative' | 'Feature' | 'Human Interest'
  | 'Political' | 'Business' | 'Sports' | 'Entertainment'
  | 'Cultural' | 'Environmental' | 'Social' | 'Opinion';

export type JournalistStyle =
  | 'Formal Broadsheet' | 'Semi-Formal' | 'Vernacular/Local' | 'Literary';

export type JournalistAudience =
  | 'Urban Educated' | 'Rural Community' | 'Youth' | 'General Public';

export type JournalistArticleType =
  | 'Hard News' | 'Soft News' | 'Feature' | 'Human Interest'
  | 'Editorial' | 'Analysis' | 'Report' | 'Interview';

export interface JournalistArticleRequest {
  language: JournalistLanguage;
  state: string;
  tone: JournalistTone;
  writingStyle: JournalistStyle;
  targetAudience: JournalistAudience;
  articleType: JournalistArticleType;
  wordLimit: number;
  sourceMaterial: string;
  specialAngle?: string;
}

export interface JournalistArticleResponse {
  headline: string;
  subHeadline: string;
  body: string;
  requestedWordLimit: number;
  actualWordCount: number;
  language: string;
  state: string;
  tone: string;
  articleType: string;
  editorialNotes?: string[];
}

/**
 * Enhanced TTS request with voice and BGM configuration.
 */
export interface EnhancedTTSRequest extends TTSRequest {
  /** Voice configuration ID for dynamic voice selection */
  voiceConfigurationId?: number;
  /** Background music ID for dynamic BGM selection */
  backgroundMusicId?: number;
  /** Whether to include background music */
  includeBackgroundMusic?: boolean;
}